import type { PayableRule } from '@spendesk/bookkeeping-core-types';

import type { I18nKey } from 'src/core/common/hooks/useTranslation';

export const getPayableRuleRequestorIds = (payableRule: PayableRule) => {
  const requestorIds: string[] = [];
  payableRule.conditions.forEach((condition) => {
    if (condition.field === 'requestor' && Array.isArray(condition.value)) {
      condition.value.forEach((requestorId) => {
        requestorIds.push(requestorId);
      });
    }
  });
  return requestorIds;
};

export const getPayableUserIdsToFetch = (
  payableRules: PayableRule[],
): string[] => {
  const userIds = payableRules
    .flatMap((payableRule) => [
      payableRule.updatedByUserId,
      payableRule.createdByUserId,
    ])
    .filter(Boolean) as string[];
  const requestorIds = payableRules.flatMap((payableRule) =>
    getPayableRuleRequestorIds(payableRule),
  );
  return [...new Set([...userIds, ...requestorIds])];
};

export const getPayableRuleSupplierIds = (payableRule: PayableRule) => {
  const supplierIds: string[] = [];
  payableRule.conditions.forEach((condition) => {
    if (condition.field === 'supplier' && Array.isArray(condition.value)) {
      condition.value.forEach((supplierId) => {
        supplierIds.push(supplierId);
      });
    }
  });
  return supplierIds;
};

export const getPayablesSupplierIdsToFetch = (
  payableRules: PayableRule[],
): string[] => {
  return [
    ...new Set(
      payableRules.flatMap((payableRule) =>
        getPayableRuleSupplierIds(payableRule),
      ),
    ),
  ];
};

export const getPayableExpenseAccountIdToFetch = (payableRule: PayableRule) => {
  return payableRule.outputs.expenseAccount ?? '';
};

export const getPayablesExpenseAccountIdsToFetch = (
  payableRules: PayableRule[],
): string[] => {
  const filteredPayableRules = payableRules
    .map((payableRule) => getPayableExpenseAccountIdToFetch(payableRule))
    .filter(Boolean);
  return [...new Set(filteredPayableRules)];
};

export const getPayableVatAccountIdToFetch = (payableRule: PayableRule) => {
  return payableRule.outputs.vatAccount ?? '';
};

export const getPayablesVatAccountIdsToFetch = (
  payableRules: PayableRule[],
): string[] => {
  const filteredPayableRules = payableRules
    .map((payableRule) => getPayableVatAccountIdToFetch(payableRule))
    .filter(Boolean);
  return [...new Set(filteredPayableRules)];
};

export const getCustomFieldIdsToFetch = (
  payableRules: PayableRule[],
): string[] => {
  const customFieldIds: string[] = [];
  payableRules.forEach((payableRule) => {
    payableRule.conditions.forEach((condition) => {
      if (
        condition.field === 'customField' &&
        typeof condition.value === 'object' &&
        !Array.isArray(condition.value) &&
        condition.value !== null
      ) {
        customFieldIds.push(condition.value.key);
      }
    });
    if (payableRule.outputs.customField) {
      Object.keys(payableRule.outputs.customField).forEach((customFieldId) => {
        customFieldIds.push(customFieldId);
      });
    }
  });
  return [...new Set(customFieldIds)];
};

export const getCustomFieldValuesIdsToFetch = (
  payableRules: PayableRule[],
): string[] => {
  const customFieldValueIds: string[] = [];
  payableRules.forEach((payableRule) => {
    payableRule.conditions.forEach((condition) => {
      if (
        condition.field === 'customField' &&
        typeof condition.value === 'object' &&
        !Array.isArray(condition.value) &&
        condition.value !== null &&
        Array.isArray(condition.value.value) &&
        condition.value.value.length > 0
      ) {
        customFieldValueIds.push(...condition.value.value);
      }
    });
    if (payableRule.outputs.customField) {
      Object.values(payableRule.outputs.customField).forEach(
        (customFieldValueId) => {
          customFieldValueIds.push(customFieldValueId);
        },
      );
    }
  });
  return [...new Set(customFieldValueIds)];
};

export type PayableType =
  | 'creditNote'
  | 'subscription'
  | 'mileageAllowance'
  | 'perDiemAllowance'
  | 'invoicePurchase'
  | 'singleCardPurchase'
  | 'physicalCardPurchase'
  | 'multiUseCardPurchase'
  | 'cardRefund'
  | 'expenseClaim';

export const getReadablePayableType = (
  type: PayableType | null | 'any',
): I18nKey | undefined => {
  switch (type) {
    case 'singleCardPurchase':
      return 'payables.panel.types.singleUseCardPurchase';
    case 'multiUseCardPurchase':
      return 'payables.panel.types.multiUseCardPurchase';
    case 'subscription':
      return 'payables.panel.types.subscriptionCardPurchase';
    case 'physicalCardPurchase':
      return 'payables.panel.types.platicCardPurchase';
    case 'invoicePurchase':
      return 'payables.panel.types.invoice';
    case 'expenseClaim':
      return 'payables.panel.types.expenseClaim';
    case 'mileageAllowance':
      return 'payables.panel.types.mileage';
    case 'perDiemAllowance':
      return 'payables.panel.types.perDiem';
    case 'creditNote':
      return 'payables.panel.types.creditNote';
    case 'cardRefund':
      return 'payables.panel.types.reversal';
    default:
      return undefined;
  }
};
